import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, interval, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { OfficeHoursStatus } from '@shared/services/office-hours/office-hours-status';
import { RemoteConfigService } from '@backend-client/services/remote-config.service';
import { TubRegionCoreHours } from '@backend-client/models/tub-region-core-hours';

@Injectable({
  providedIn: 'root',
})
export class OfficeHoursService {
  private destroyRef = inject(DestroyRef);
  private remoteConfigService = inject(RemoteConfigService);

  private schedule: TubRegionCoreHours;
  private officeHours$ = new BehaviorSubject<OfficeHoursStatus>({
    isOfficeHours: false,
    startTime: { hours: 0, minutes: 0 },
    endTime: { hours: 0, minutes: 0 },
  });

  private readonly ONE_MINUTE_IN_MILLISECONDS = 60000;

  private isInitialised = false;

  /**
   * Method to initialise and periodically refresh the schedule.
   * This is called from the AppOfficeHours component which is only displayed for therapist accounts
   */
  public initialiseSchedule(): void {
    if (this.isInitialised) {
      return;
    }
    // Set up periodic fetching of the schedule
    this.setupFetchSchedule();

    // Initial fetch
    this.refreshSchedule();

    this.isInitialised = true;
  }

  private fetchSchedule(): Observable<TubRegionCoreHours> {
    return this.remoteConfigService.RemoteConfigGetRegionCoreHours();
  }

  private checkTime(): void {
    if (!this.schedule) {
      return;
    }

    const now = this.getCurrentTimeInTimezone(this.schedule.timeZone);
    const nowMinutes = now.hours * 60 + now.minutes;
    const startMinutes = this.schedule.startTime.hours * 60 + this.schedule.startTime.minutes;
    const endMinutes = this.schedule.endTime.hours * 60 + this.schedule.endTime.minutes;

    const isOfficeHours = nowMinutes >= startMinutes && nowMinutes < endMinutes;

    this.officeHours$.next({
      isOfficeHours,
      startTime: this.schedule.startTime,
      endTime: this.schedule.endTime,
    });
  }

  private getCurrentTimeInTimezone(timeZone: string): { hours: number; minutes: number } {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(new Date());

    return {
      hours: Number(parts.find((p) => p.type === 'hour')?.value ?? 0),
      minutes: Number(parts.find((p) => p.type === 'minute')?.value ?? 0),
    };
  }

  public getOfficeHours$(): Observable<OfficeHoursStatus> {
    return this.officeHours$.asObservable();
  }

  // Method to refresh the schedule immediately
  public refreshSchedule(): void {
    this.fetchSchedule().subscribe((schedule) => {
      this.schedule = schedule;
      this.checkTime();
    });
  }

  private setupFetchSchedule(): void {
    interval(this.ONE_MINUTE_IN_MILLISECONDS)
      .pipe(
        switchMap(() => this.fetchSchedule()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((schedule) => {
        this.schedule = schedule;
        this.checkTime();
      });
  }
}
